import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CloseIcon, UserIcon } from '../components/shared/Icons';
import { fetchSyUsers, syCorrection } from '../utils/api';

/* ============================================================================
 * ChangeCleaner — supervisor/admin correction tool for South Yarra.
 *   · icon button (renders next to HelpButton via renderExtraActions)
 *   · modal lists ONLY records whose Clean Date is in the CURRENT month
 *   · clicking a record opens a 3-field editor: Cleaner, Email, Complete Date
 *   · Cleaner + Email are pick-only (no free text) from the login table:
 *       two-tier rows — Name with Email underneath (and Email with Name
 *       underneath). Picking either fills BOTH fields; the Cleaner field is
 *       populated with User_Name (first name), per the app convention.
 *   · works on approved records too — that's the point of the tool; the
 *     backend logs every save as "<user> [correction]" in SY Change Log
 * ==========================================================================*/

const displayDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return d ? `${d}/${m}/${y}` : iso;
};
const pad = (n) => String(n).padStart(2, '0');
const currentYM = () => {
  const t = new Date();
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}`;
};
const monthLabel = () =>
  new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

/* ---------- two-tier pick-only person select ---------- */
const PersonSelect = ({ label, valueTop, primary, people, onPick }) => {
  // primary: "name" | "email" — which line shows on top in the dropdown
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().replace(/\s+/g, '');
    if (!s) return people;
    return people.filter((p) =>
      `${p.name}${p.userName}${p.email}`.toLowerCase().replace(/\s+/g, '').includes(s));
  }, [people, q]);

  return (
    <div className="fr-modal-field" ref={boxRef} style={{ position: 'relative' }}>
      <label className="fr-modal-label">{label}</label>
      <button type="button" className="fr-modal-input"
        style={{ textAlign: 'left', cursor: 'pointer', width: '100%',
          color: valueTop ? undefined : 'rgba(255,255,255,0.35)' }}
        onClick={() => { setOpen((p) => !p); setQ(''); }}>
        {valueTop || `Select ${label.toLowerCase()}…`}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40,
          marginTop: 4, borderRadius: 10, overflow: 'hidden',
          background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)',
          border: '1px solid rgba(34,211,238,0.35)', boxShadow: '0 18px 50px rgba(0,0,0,0.55)' }}>
          <input autoFocus className="fr-modal-input" placeholder="Search name or email…"
            value={q} onChange={(e) => setQ(e.target.value)}
            style={{ borderRadius: 0, border: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.1)' }} />
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px 14px', fontSize: '0.8rem',
                color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>No matches.</div>
            ) : filtered.map((p) => (
              <button key={p.email} type="button"
                onClick={() => { onPick(p); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                  padding: '8px 14px', background: 'none', border: 'none',
                  borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34,211,238,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#22d3ee' }}>
                  {primary === 'name' ? p.name : p.email}
                </span>
                <span style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' }}>
                  {primary === 'name' ? p.email : p.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- the modal ---------- */
const ChangeCleanerModal = ({ items, onClose, onSaved, onToast }) => {
  const ym = currentYM();
  const rows = useMemo(
    () => (items || []).filter((it) => String(it[3] || '').startsWith(ym))
      .sort((a, b) => String(a[3]).localeCompare(String(b[3])) ||
        String(a[2]).localeCompare(String(b[2]), undefined, { numeric: true })),
    [items, ym]);

  const [people, setPeople] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [sel, setSel] = useState(null);            // the row being edited
  const [cleaner, setCleaner] = useState('');      // User_Name
  const [email, setEmail] = useState('');
  const [completeDate, setCompleteDate] = useState('');
  const [saving, setSaving] = useState(false);
  

  useEffect(() => {
    fetchSyUsers().then((res) => {
      setPeople(res.status === 'success' ? res.users || [] : []);
      setLoadingPeople(false);
    });
  }, []);

  const openEditor = (it) => {
    setSel(it);
    
    setCleaner(it[15] === 'Cleaner Name' ? '' : (it[15] || ''));
    setEmail(it[18] === 'enteryouremail@yahoo.com.au' ? '' : (it[18] || ''));
    setCompleteDate(it[29] || '');
  };

  // date window: clean date → end of the clean date's month
  const dateMin = sel ? sel[3] : undefined;
  const dateMax = useMemo(() => {
    if (!sel?.[3]) return undefined;
    const [y, m] = String(sel[3]).split('-').map(Number);
    return `${y}-${pad(m)}-${pad(new Date(y, m, 0).getDate())}`;
  }, [sel]);

  const pick = (p) => { setCleaner(p.userName); setEmail(p.email); };

  const save = async () => {
    if (!cleaner || !email) {
      onToast({ message: 'Pick a cleaner from the list first.', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const res = await syCorrection({
        recordId: sel[0], cleaner, email, completeDate,
      });
      if (res.status === 'success') {
        onToast({ message: `Room ${sel[2]} corrected (change #${res.changeNo}).`, type: 'success' });
        setSel(null);
        onSaved();                                 // refetch behind the modal
      } else {
        onToast({ message: res.error || 'Correction failed.', type: 'error' });
      }
    } catch (err) {
      onToast({ message: err.message || 'Correction failed.', type: 'error' });
    }
    setSaving(false);
  };

  /* back to unclaimed — route owns the defaults; one click, recoverable via correction */
  const resetJob = async () => {
    setSaving(true);
    try {
      const res = await syCorrection({ recordId: sel[0], reset: true });
      if (res.status === 'success') {
        onToast({ message: `Room ${sel[2]} reset to Default (change #${res.changeNo}).`, type: 'success' });
        setSel(null);
        onSaved();
      } else {
        onToast({ message: res.error || 'Reset failed.', type: 'error' });
      }
    } catch (err) {
      onToast({ message: err.message || 'Reset failed.', type: 'error' });
    }
    setSaving(false);
  };

  return (
    <div className="fr-modal-overlay" onClick={onClose}>
      <div className="fr-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="fr-modal-handle" />
        <div className="fr-modal-header">
          <h3 className="fr-modal-title">
            {sel ? `Change Cleaner — Room ${sel[2]}` : `Change Cleaner — ${monthLabel()}`}
          </h3>
          <button className="fr-modal-close" onClick={sel ? () => setSel(null) : onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="fr-modal-body">
          {!sel ? (
            <>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', marginBottom: 10 }}>
                Current-month records only.
              </p>
              {rows.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', fontStyle: 'italic',
                  color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                  No records with a {monthLabel()} clean date in this current view.
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', columnGap: 8, padding: '4px 8px 8px',
                    gridTemplateColumns: '0.7fr 1.2fr 0.9fr 1fr 1.4fr 0.9fr',
                    borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                    {['Room', 'Type', 'Clean Date', 'Cleaner', 'Email', 'Complete Date'].map((h) => (
                      <span key={h} style={{ fontSize: '0.62rem', textTransform: 'uppercase',
                        letterSpacing: '0.05em', color: 'rgba(255,255,255,0.45)', fontWeight: 700,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h}</span>
                    ))}
                  </div>
                  <div style={{ maxHeight: '46vh', overflowY: 'auto' }}>
                    {rows.map((it, i) => (
                      <button key={it[0] || i} type="button" onClick={() => openEditor(it)}
                        style={{ display: 'grid', width: '100%', textAlign: 'left', cursor: 'pointer',
                          gridTemplateColumns: '0.7fr 1.2fr 0.9fr 1fr 1.4fr 0.9fr',
                          columnGap: 8, padding: '9px 8px', alignItems: 'center',
                          background: 'none', border: 'none',
                          borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34,211,238,0.06)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                        <span style={{ fontSize: '0.84rem', fontWeight: 700, color: '#22d3ee' }}>{it[2]}</span>
                        <span style={cellStyle}>{it[5] || '—'}</span>
                        <span style={cellStyle}>{displayDate(it[3])}</span>
                        <span style={{ ...cellStyle,
                          color: it[15] === 'Cleaner Name' ? 'rgba(255,255,255,0.35)' : '#e2e8f0',
                          fontStyle: it[15] === 'Cleaner Name' ? 'italic' : 'normal' }}>
                          {it[15] === 'Cleaner Name' ? 'Unclaimed' : it[15]}
                        </span>
                        <span style={cellStyle}>
                          {it[18] === 'enteryouremail@yahoo.com.au' ? '—' : it[18]}
                        </span>
                        <span style={cellStyle}>{it[29] ? displayDate(it[29]) : '—'}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="fr-modal-field">
                <label className="fr-modal-label">Clean Date</label>
                <div className="fr-modal-value">{displayDate(sel[3])}</div>
              </div>
              {loadingPeople ? (
                <div style={{ padding: 16, textAlign: 'center',
                  color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Loading people…</div>
              ) : (
                <>
                  <PersonSelect label="Cleaner" valueTop={cleaner} primary="name"
                    people={people} onPick={pick} />
                  <PersonSelect label="Email" valueTop={email} primary="email"
                    people={people} onPick={pick} />
                </>
              )}
              <div className="fr-modal-field">
                <label className="fr-modal-label">Complete Date</label>
                <input type="date" className="fr-modal-input" value={completeDate}
                  min={dateMin} max={dateMax}
                  onChange={(e) => setCompleteDate(e.target.value)} />
              </div>
              <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
                Saved as a logged correction — approval status is not affected.
              </p>
            </>
          )}
        </div>

        <div className="fr-modal-actions">
          <button className="fr-modal-btn fr-btn-cancel"
            onClick={sel ? () => { setSel(null); } : onClose} disabled={saving}>
            {sel ? 'Back' : 'Close'}
          </button>
          {sel && (
            <button className="fr-modal-btn fr-btn-cancel" onClick={resetJob}
              disabled={saving}
              style={{ background: 'rgba(251,191,36,0.1)',
                borderColor: 'rgba(251,191,36,0.35)', color: '#fbbf24' }}>
              ↺ Reset to Default
            </button>
          )}
          {sel && (
            <button className="fr-modal-btn fr-btn-submit" onClick={save}
              disabled={saving || loadingPeople}>
              {saving ? 'Saving…' : 'Save Correction'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const cellStyle = { fontSize: '0.8rem', color: '#e2e8f0',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/* ---------- icon button + modal wrapper ---------- */
const ChangeCleaner = ({ items, visible, onSaved, onToast }) => {
  const [open, setOpen] = useState(false);
  if (!visible) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        title={`Change Cleaner — ${monthLabel()} only`}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
          borderRadius: 8, background: 'rgba(34,211,238,0.1)',
          border: '1px solid rgba(34,211,238,0.35)', color: '#22d3ee',
          fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
          transition: 'all 0.15s ease' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34,211,238,0.18)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(34,211,238,0.1)'; }}>
        <UserIcon />＋
      </button>
      {open && (
        <ChangeCleanerModal items={items} onToast={onToast}
          onClose={() => setOpen(false)}
          onSaved={onSaved} />
      )}
    </>
  );
};

export default ChangeCleaner;